<script setup lang="ts">
type FormType = 'text' | 'password' | 'email'
type InputMode = 'email'
type AutoCapitalize = 'off'

type Props = {
  modelValue: string
  placeholder?: string
  disabled?: boolean
  type?: FormType
  inputMode?: InputMode
  autoFocus?: boolean
  border?: boolean
  autoCapitalize?: AutoCapitalize
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  type: 'text',
  inputMode: undefined,
  autoFocus: false,
  border: true,
  autoCapitalize: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
  enter: []
}>()

const value = computed({
  get: () => props.modelValue || '',
  set: (value: string) => emit('update:modelValue', value),
})

const vFocus = {
  mounted: (el: HTMLInputElement) => {
    if (props.autoFocus) {
      el.focus()
    }
  },
}
</script>

<template>
  <div>
    <label
      class="text-input"
      :class="{
        'no-border': !border,
      }"
    >
      <slot name="icon" />
      <input
        v-model="value"
        v-focus
        class="text-input-inner"
        :type="type"
        :input-mode="inputMode"
        :autocapitalize="autoCapitalize"
        :placeholder="placeholder"
        :disabled="disabled"
        @blur="emit('blur')"
        @keypress.enter="emit('enter')"
      />
    </label>
  </div>
</template>

<style scoped lang="scss">
// Edge で password input で表示されるアイコンを非表示にする
::-ms-reveal {
  display: none;
}

// bulmaのinputクラスと同じになるように設定している
.text-input {
  display: flex;
  gap: spacingToken.$spacing-xxs;
  align-items: center;
  width: 100%;
  max-width: 100%;
  background: var(--semantic-tokens-neutral-default-background);
  border: 1px solid var(--semantic-tokens-neutral-weak-border);
  border-radius: 4px;
  box-shadow: inset 0 0.0625em 0.125em rgba(10, 10, 10, 0.05);
  color: var(--semantic-tokens-neutral-default-text);
  height: 2.5em;
  padding-bottom: calc(0.5em - 1px);
  padding-left: calc(0.75em - 1px);
  padding-right: calc(0.75em - 1px);
  padding-top: calc(0.5em - 1px);

  &.no-border {
    border: none;
    box-shadow: none;
  }

  &:hover {
    border-color: var(--semantic-tokens-neutral-default-border);
  }

  &:active,
  &:focus-within {
    border-color: var(--semantic-tokens-brand-strong-text);
    // フォーカスリングも枠線と同じ新パレット(brand-strong-text #1b3fb0)の半透明に合わせる
    box-shadow: 0 0 0 0.125em rgba(27, 63, 176, 0.25);
    outline: none;

    &.no-border {
      box-shadow: none;
    }
  }

  &::placeholder {
    color: rgba(54, 54, 54, 0.3);
  }
}
.text-input-inner {
  width: 100%;
  height: 100%;
  border: none;
  box-shadow: none;
  background: transparent;
  font-size: 1rem;
  color: inherit;

  &::-webkit-search-cancel-button {
    display: none;
  }

  &:focus {
    outline: none;
  }

  &.disabled {
    cursor: not-allowed;
  }
}
</style>
